import React, { useEffect, useState } from 'react';
import { Search, Bell, Settings, ChevronDown, Download, ChevronRight, Star, Upload, ArrowLeftRight, Coins, Eye, EyeOff } from 'lucide-react';
interface HeaderProps {
  currentMarket?: string;
  currentPrice?: number;
  indexPrice?: number;
  priceChange?: number;
  priceChangePercent?: number;
  volume24h?: string;
  volume24hBTC?: string;
  accountBalance?: number;
}
export function Header({
  currentMarket = 'BTC/USD',
  currentPrice = 95441.1,
  indexPrice = 95453.7,
  priceChange = -1.94,
  priceChangePercent = -1.94,
  volume24h = '205M',
  volume24hBTC = '2.15K',
  accountBalance = 79.19
}: HeaderProps) {
  const [priceTickColor, setPriceTickColor] = useState<'success' | 'danger' | null>(null);
  const [prevPrice, setPrevPrice] = useState(currentPrice);
  const [isMarketDropdownOpen, setIsMarketDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState('spot');
  // Account balances matching PortfolioOverview
  const totalPortfolioValue = 50234.67; // USD
  const portfolioChange = -234.56; // USD
  const portfolioChangePercent = -0.47; // %
  const accounts = {
    main: {
      balance: 50234.67,
      availableMargin: 2450.32
    },
    spot: {
      balance: 45123.89,
      upnl: 1250.45
    },
    margin: {
      balance: -350.78,
      usedMargin: 2550.12
    },
    futures: {
      balance: 0.0,
      buyingPower: 15000.0,
      positionsUpnl: 0.0
    },
    inverseFutures: {
      balance: 0.0,
      positionsUpnl: 0.0
    },
    earn: {
      balance: 0.0,
      totalRewards: 123.45
    }
  };
  useEffect(() => {
    if (currentPrice !== prevPrice) {
      setPriceTickColor(currentPrice > prevPrice ? 'success' : 'danger');
      setPrevPrice(currentPrice);
      const timer = setTimeout(() => {
        setPriceTickColor(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentPrice, prevPrice]);
  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  };
  const priceColor = priceTickColor === 'success' ? 'text-success' : priceTickColor === 'danger' ? 'text-danger' : 'text-text-primary';
  return <header className="flex items-center justify-between px-4 py-2.5 bg-background-primary border-b border-border-subtle" data-id="element-764">
      {/* Left: Market Selector + Stats */}
      <div className="flex items-center gap-6" data-id="element-765">
        {/* Market Search Dropdown */}
        <div className="relative" data-id="element-766">
          <button onClick={() => setIsMarketDropdownOpen(!isMarketDropdownOpen)} className="flex items-center gap-2 px-3 py-2 bg-background-secondary hover:bg-background-elevated rounded-lg transition-colors" data-id="element-767">
            <Search size={16} className="text-text-tertiary" data-id="element-768" />
            <img src="https://cryptologos.cc/logos/bitcoin-btc-logo.svg" alt="BTC" className="w-7 h-6 object-contain" data-id="element-769" />
            <span className="font-semibold text-sm text-text-primary" data-id="element-770">
              BTC<span className="text-text-secondary" data-id="element-771">/USD</span>
            </span>
            <span className="text-xs text-text-tertiary bg-background-elevated px-1.5 py-0.5 rounded" data-id="element-772">
              10x
            </span>
            <span className="text-text-tertiary" data-id="element-773">⌘K</span>
          </button>

          {isMarketDropdownOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsMarketDropdownOpen(false)} data-id="element-774" />
              <div className="absolute left-0 top-full mt-2 w-[800px] bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-20 overflow-hidden flex flex-col" data-id="element-775">
                {/* Top Tabs - Added depth with background and border */}
                <div className="flex items-center gap-1 px-2 py-2 bg-background-secondary border-b border-border-subtle" data-id="element-776">
                  <button onClick={() => setActiveTab('favorites')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'favorites' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-777">
                    <Star size={16} className={activeTab === 'favorites' ? 'fill-current' : ''} data-id="element-778" />
                    Favorites
                  </button>
                  <button onClick={() => setActiveTab('spot')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'spot' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-779">
                    Spot & Margin
                  </button>
                  <button onClick={() => setActiveTab('futures')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'futures' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-780">
                    Futures
                  </button>
                  <button onClick={() => setActiveTab('xstocks')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'xstocks' ? 'bg-background-elevated text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-781">
                    xStocks
                    <span className="text-[10px] bg-brand-purple text-white px-1.5 py-0.5 rounded font-bold tracking-wide" data-id="element-782">
                      NEW
                    </span>
                  </button>
                </div>

                {/* Search Bar & Filters Container */}
                <div className="bg-background-elevated" data-id="element-783">
                  <div className="p-4 border-b border-border-subtle" data-id="element-784">
                    <div className="flex items-center gap-3 px-4 py-3 bg-background-primary border border-border-default focus-within:border-brand-purple focus-within:ring-1 focus-within:ring-brand-purple rounded-full transition-all shadow-inner" data-id="element-785">
                      <Search size={18} className="text-text-tertiary" data-id="element-786" />
                      <input type="text" placeholder="Search markets" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none" data-id="element-787" />
                      <div className="h-4 w-px bg-border-subtle mx-2" data-id="element-788" />
                      <button className="flex items-center gap-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors" data-id="element-789">
                        Quote: USD <ChevronDown size={12} data-id="element-790" />
                      </button>
                    </div>
                  </div>

                  {/* Filter Tabs - Sub-navigation */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle overflow-x-auto scrollbar-hide" data-id="element-791">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-primary bg-background-secondary border border-border-subtle rounded-full hover:border-text-secondary transition-colors whitespace-nowrap" data-id="element-792">
                      <span data-id="element-793">🔥</span> Hot
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-794">
                      <span data-id="element-795">🚀</span> New Listings
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-796">
                      <span data-id="element-797">⚡</span> Margin
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-798">
                      <span data-id="element-799">%</span> Earn
                    </button>
                    <div className="h-4 w-px bg-border-subtle mx-1" data-id="element-800" />
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-801">
                      Categories <ChevronDown size={12} data-id="element-802" />
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-full transition-colors whitespace-nowrap" data-id="element-803">
                      Ecosystems <ChevronDown size={12} data-id="element-804" />
                    </button>
                  </div>
                </div>

                {/* Market List - Aligned with HoldingsTable design */}
                <div className="max-h-[400px] overflow-y-auto bg-background-elevated" data-id="element-805">
                  {/* Header */}
                  <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider border-b border-border-subtle sticky top-0 bg-background-elevated/95 backdrop-blur-sm z-10" data-id="element-806">
                    <div className="pl-2" data-id="element-807">Market</div>
                    <div className="text-right" data-id="element-808">Price</div>
                    <div className="text-right" data-id="element-809">24h Change</div>
                    <div className="text-right" data-id="element-810">Volume</div>
                    <div data-id="element-811"></div>
                  </div>

                  {/* Market Rows */}
                  {[{
                logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
                name: 'BTC',
                quote: 'USD',
                leverage: '10x',
                price: '95,532.3',
                change: '-2.03',
                volume: '165M',
                currency: 'CHF',
                favorite: true
              }, {
                logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
                name: 'USDT',
                quote: 'USD',
                leverage: '2x',
                price: '0.99985',
                change: '-0.02',
                volume: '140M',
                currency: 'CHF',
                favorite: false
              }, {
                logo: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.svg',
                name: 'USDC',
                quote: 'EUR',
                leverage: '3x',
                price: '0.8606',
                change: '0.19',
                volume: '86.9M',
                currency: 'CHF',
                favorite: false
              }, {
                logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
                name: 'ETH',
                quote: 'USD',
                leverage: '10x',
                price: '3,287.34',
                change: '-2.31',
                volume: '84.4M',
                currency: 'CHF',
                favorite: true
              }, {
                logo: 'https://cryptologos.cc/logos/solana-sol-logo.svg',
                name: 'SOL',
                quote: 'USD',
                leverage: '10x',
                price: '141.33',
                change: '-3.80',
                volume: '47.3M',
                currency: 'CHF',
                favorite: true
              }].map((market, idx) => <a key={idx} href="/trading" className="grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-4 hover:bg-background-elevated/30 transition-colors items-center group border-b border-border-subtle last:border-0" data-id="element-812">
                      <div className="flex items-center gap-3 pl-2" data-id="element-813">
                        <img src={market.logo} alt={market.name} className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-200" data-id="element-814" />
                        <div className="text-left" data-id="element-815">
                          <div className="font-bold text-sm text-text-primary group-hover:text-brand-purple transition-colors" data-id="element-816">
                            {market.name}
                            <span className="text-text-secondary font-normal group-hover:text-brand-purple-light/70" data-id="element-817">
                              /{market.quote}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-0.5" data-id="element-818">
                            <span className="text-[10px] font-medium text-text-tertiary bg-background-secondary px-1.5 py-0.5 rounded border border-border-subtle" data-id="element-819">
                              {market.leverage}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right font-numbers text-sm font-medium text-text-primary" data-id="element-820">
                        {market.price}{' '}
                        <span className="text-text-secondary text-xs font-normal" data-id="element-821">
                          {market.quote}
                        </span>
                      </div>
                      <div className={`text-right font-numbers text-sm font-medium ${parseFloat(market.change) >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-822">
                        {parseFloat(market.change) >= 0 ? '+' : ''}
                        {market.change}%
                      </div>
                      <div className="text-right font-numbers text-sm text-text-secondary" data-id="element-823">
                        {market.volume}{' '}
                        <span className="text-text-tertiary text-xs" data-id="element-824">
                          {market.currency}
                        </span>
                      </div>
                      <button className="p-2 hover:bg-background-elevated rounded-full transition-colors" onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Toggle favorite logic here
                }} data-id="element-825">
                        <Star size={16} className={market.favorite ? 'fill-warning text-warning' : 'text-text-tertiary hover:text-text-primary'} data-id="element-826" />
                      </button>
                    </a>)}
                </div>
              </div>
            </>}
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-8" data-id="element-827">
          {/* Last Price */}
          <div data-id="element-828">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-829">
              LAST PRICE
            </div>
            <div className={`font-mono text-base font-semibold transition-colors duration-300 ${priceColor}`} data-id="element-830">
              {formatNumber(currentPrice)}{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-831">
                USD
              </span>
            </div>
          </div>

          {/* Index Price */}
          <div data-id="element-832">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-833">
              INDEX PRICE
            </div>
            <div className="font-mono text-base font-semibold text-text-primary" data-id="element-834">
              {formatNumber(indexPrice)}{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-835">
                USD
              </span>
            </div>
          </div>

          {/* 24h Change */}
          <div data-id="element-836">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-837">
              24H CHANGE
            </div>
            <div className="font-mono text-base font-semibold text-[#E85D75]" data-id="element-838">
              {priceChangePercent.toFixed(2)}%
            </div>
          </div>

          {/* 24h Volume */}
          <div data-id="element-839">
            <div className="text-[10px] text-text-tertiary uppercase tracking-wider mb-0.5" data-id="element-840">
              24H VOLUME
            </div>
            <div className="font-mono text-base font-semibold text-text-primary" data-id="element-841">
              {volume24hBTC}{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-842">
                BTC
              </span>{' '}
              <span className="text-text-secondary text-xs font-normal" data-id="element-843">
                {volume24h}USD
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Account & Actions */}
      <div className="flex items-center gap-3" data-id="element-844">
        <button className="p-2 hover:bg-background-elevated rounded transition-colors relative" data-id="element-845">
          <Bell size={18} className="text-text-secondary" data-id="element-846" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" data-id="element-847" />
        </button>

        <button className="p-2 hover:bg-background-elevated rounded transition-colors" data-id="element-848">
          <Star size={18} className="text-text-secondary" data-id="element-849" />
        </button>

        <div className="h-6 w-px bg-border-subtle" data-id="element-850" />

        <div className="flex items-center gap-3" data-id="element-851">
          <div className="relative" data-id="element-852">
            <button onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)} className="flex items-center gap-2 px-3 py-2 hover:bg-background-elevated rounded-lg transition-colors" data-id="element-853">
              <div className="text-right" data-id="element-854">
                <div className="text-xs text-text-secondary" data-id="element-855">Balance</div>
                <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-856">
                  {showBalance ? totalPortfolioValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }) : '••••••'}{' '}
                  <span className="text-text-secondary text-xs" data-id="element-857">USD</span>
                </div>
              </div>
              <ChevronDown size={16} className="text-text-secondary" data-id="element-858" />
            </button>

            {isAccountDropdownOpen && <>
                <div className="fixed inset-0 z-10" onClick={() => setIsAccountDropdownOpen(false)} data-id="element-859" />
                <div className="absolute right-0 top-full mt-2 w-[480px] bg-[#0D0F14] border border-border-subtle rounded-xl shadow-2xl z-20 overflow-hidden" data-id="element-860">
                  {/* Header */}
                  <div className="p-6 bg-[#1A1D26] border-b border-border-subtle" data-id="element-861">
                    <div className="flex items-center justify-between mb-3" data-id="element-862">
                      <div className="flex items-center gap-3" data-id="element-863">
                        <span className="text-sm text-text-secondary" data-id="element-864">
                          Total portfolio value
                        </span>
                        <button onClick={() => setShowBalance(!showBalance)} className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-865">
                          {showBalance ? <EyeOff size={18} className="text-text-secondary" data-id="element-866" /> : <Eye size={18} className="text-text-secondary" data-id="element-867" />}
                        </button>
                      </div>
                      <div className="font-mono text-2xl font-bold text-text-primary" data-id="element-868">
                        {showBalance ? totalPortfolioValue.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) : '••••••'}{' '}
                        <span className="text-text-secondary text-base font-normal" data-id="element-869">
                          USD
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4" data-id="element-870">
                      <span className="text-sm text-text-secondary" data-id="element-871">
                        Since yesterday
                      </span>
                      <div className="text-sm font-mono text-[#E85D75]" data-id="element-872">
                        {portfolioChange.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                        <span className="text-text-secondary" data-id="element-873">USD</span> (
                        {portfolioChangePercent.toFixed(2)}%)
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-5 gap-2" data-id="element-874">
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-875">
                        <Download size={20} className="text-white" data-id="element-876" />
                        <span className="text-xs font-medium text-white" data-id="element-877">
                          Deposit
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-878">
                        <Upload size={20} className="text-white" data-id="element-879" />
                        <span className="text-xs font-medium text-white" data-id="element-880">
                          Withdraw
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-881">
                        <ArrowLeftRight size={20} className="text-white" data-id="element-882" />
                        <span className="text-xs font-medium text-white" data-id="element-883">
                          Convert
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-884">
                        <ArrowLeftRight size={20} className="text-white" data-id="element-885" />
                        <span className="text-xs font-medium text-white" data-id="element-886">
                          Transfer
                        </span>
                      </button>
                      <button className="flex flex-col items-center gap-2 p-3 bg-[#3D3555] hover:bg-[#4A4066] rounded-lg transition-colors" data-id="element-887">
                        <Coins size={20} className="text-white" data-id="element-888" />
                        <span className="text-xs font-medium text-white" data-id="element-889">
                          Earn
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Account Sections */}
                  <div className="max-h-[500px] overflow-y-auto" data-id="element-890">
                    {/* Main Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-891">
                      <div className="flex items-center justify-between mb-2" data-id="element-892">
                        <div className="flex items-center gap-2" data-id="element-893">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-894">
                            Main
                          </span>
                          <ChevronDown size={14} className="text-text-secondary" data-id="element-895" />
                        </div>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-896">
                          {accounts.main.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-897">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-898">
                        <span className="text-text-secondary" data-id="element-899">
                          Available margin
                        </span>
                        <span className="font-mono text-text-primary" data-id="element-900">
                          {accounts.main.availableMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-901">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Spot Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-902">
                      <div className="flex items-center justify-between mb-2" data-id="element-903">
                        <span className="text-sm font-semibold text-text-primary" data-id="element-904">
                          Spot
                        </span>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-905">
                          {accounts.spot.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-906">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-907">
                        <span className="text-text-secondary" data-id="element-908">
                          Balances UP&L
                        </span>
                        <span className="font-mono text-success" data-id="element-909">
                          +
                          {accounts.spot.upnl.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-910">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Margin Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-911">
                      <div className="flex items-center justify-between mb-2" data-id="element-912">
                        <div className="flex items-center gap-2" data-id="element-913">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-914">
                            Margin
                          </span>
                          <span className="w-2 h-2 rounded-full bg-success" data-id="element-915" />
                        </div>
                        <div className="font-mono text-sm font-semibold text-danger" data-id="element-916">
                          {accounts.margin.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-917">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-918">
                        <span className="text-text-secondary" data-id="element-919">Used margin</span>
                        <span className="font-mono text-text-primary" data-id="element-920">
                          {accounts.margin.usedMargin.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-921">USD</span>
                        </span>
                      </div>
                    </div>

                    {/* Futures Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-922">
                      <div className="flex items-center justify-between mb-2" data-id="element-923">
                        <div className="flex items-center gap-2" data-id="element-924">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-925">
                            Futures
                          </span>
                          <button className="text-xs text-brand-purple-lighter hover:text-brand-purple-light flex items-center gap-1 transition-colors" data-id="element-926">
                            Transfer funds
                          </button>
                        </div>
                        <div className="text-right" data-id="element-927">
                          <div className="text-xs text-text-secondary mb-1" data-id="element-928">
                            Buying Power
                          </div>
                          <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-929">
                            ≈
                            {accounts.futures.buyingPower.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            <span className="text-text-secondary text-xs" data-id="element-930">
                              USD
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-2" data-id="element-931">
                        <span className="text-text-secondary" data-id="element-932">
                          Positions UP&L
                        </span>
                        <span className="font-mono text-text-secondary" data-id="element-933">
                          No open positions
                        </span>
                      </div>
                    </div>

                    {/* Inverse Futures Account */}
                    <div className="p-4 border-b border-border-subtle hover:bg-background-secondary transition-colors" data-id="element-934">
                      <div className="flex items-center justify-between mb-2" data-id="element-935">
                        <div className="flex items-center gap-2" data-id="element-936">
                          <span className="text-sm font-semibold text-text-primary" data-id="element-937">
                            Inverse Futures
                          </span>
                          <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-938">
                            {accounts.inverseFutures.balance.toFixed(2)}{' '}
                            <span className="text-text-secondary text-xs" data-id="element-939">
                              USD
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs" data-id="element-940">
                          <span className="text-text-secondary" data-id="element-941">
                            Positions UP&L
                          </span>
                          <span className="font-mono text-text-secondary" data-id="element-942">
                            No open positions
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Earn Account */}
                    <div className="p-4 hover:bg-background-secondary transition-colors" data-id="element-943">
                      <div className="flex items-center justify-between mb-2" data-id="element-944">
                        <span className="text-sm font-semibold text-text-primary" data-id="element-945">
                          Earn
                        </span>
                        <div className="font-mono text-sm font-semibold text-text-primary" data-id="element-946">
                          {accounts.earn.balance.toFixed(2)}{' '}
                          <span className="text-text-secondary text-xs" data-id="element-947">
                            USD
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs" data-id="element-948">
                        <span className="text-text-secondary" data-id="element-949">
                          Total rewards
                        </span>
                        <span className="font-mono text-success" data-id="element-950">
                          +
                          {accounts.earn.totalRewards.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                          <span className="text-text-secondary" data-id="element-951">USD</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-brand-purple hover:bg-brand-purple-light rounded transition-colors text-white" data-id="element-952">
            <Download size={16} data-id="element-953" />
            <span className="text-sm font-medium" data-id="element-954">Deposit</span>
          </button>
        </div>

        <button className="p-2 hover:bg-background-elevated rounded transition-colors" data-id="element-955">
          <Settings size={18} className="text-text-secondary" data-id="element-956" />
        </button>
      </div>
    </header>;
}